import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useAuthContext } from "@asgardeo/auth-react";
import ReactMarkdown from "react-markdown";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import SearchLayout from "../../layouts/SearchLayout";
import {
  decodeCaseNumberFromPath,
  encodeCaseNumberForPath,
  normalizeCaseNumbers,
} from "../../utils/caseNumber";

const markdownComponents = {
  h1: ({ node, ...props }) => (
    <h1
      className="mt-8 first:mt-0 text-xl font-display font-semibold text-pl-text-primary"
      {...props}
    />
  ),
  h2: ({ node, ...props }) => (
    <h2
      className="mb-0 mt-8 first:mt-0 border-b border-pl-border pb-0 text-18 font-display font-semibold text-pl-text-primary"
      {...props}
    />
  ),
  h3: ({ node, ...props }) => (
    <h3 className="mt-8 mb-2 text-16 font-semibold text-pl-text-primary" {...props} />
  ),
  p: ({ node, ...props }) => (
    <p className="mt-4 leading-8 text-pl-text-secondary" {...props} />
  ),
  strong: ({ node, ...props }) => (
    <strong className="font-semibold text-pl-text-secondary" {...props} />
  ),
  ul: ({ node, ...props }) => (
    <ul className="mt-4 list-disc pl-6 text-pl-text-secondary" {...props} />
  ),
  ol: ({ node, ...props }) => (
    <ol className="mt-4 list-decimal pl-6 text-pl-text-secondary" {...props} />
  ),
  li: ({ node, ...props }) => <li className="mt-2" {...props} />,
  blockquote: ({ node, ...props }) => (
    <blockquote
      className="mt-6 border-l-4 border-pl-accent/50 pl-4 italic text-pl-text-secondary"
      {...props}
    />
  ),
  a: ({ node, ...props }) => (
    <a
      className="text-pl-accent underline underline-offset-2"
      target="_blank"
      rel="noreferrer"
      {...props}
    />
  ),
  code: ({ inline, node, children, ...props }) =>
    inline ? (
      <code className="rounded bg-pl-bg-subtle px-1 py-0.5 text-sm" {...props}>
        {children}
      </code>
    ) : (
      <code
        className="block overflow-x-auto rounded-lg bg-pl-bg-subtle p-4 text-sm"
        {...props}
      >
        {children}
      </code>
    ),
};

function decodeCaseNumberFromParam(value) {
  if (!value) return "";
  return decodeCaseNumberFromPath(value);
}

function formatDate(date) {
  const parsed = new Date(date);

  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function buildMetaDescription({ parties, caseNumber, dateLabel }) {
  const segments = [];

  if (parties) {
    segments.push(`Citation record for ${parties}.`);
  } else if (caseNumber) {
    segments.push(`Citation record for ${caseNumber}.`);
  }

  if (caseNumber) {
    segments.push(`Case number: ${caseNumber}.`);
  }

  if (dateLabel) {
    segments.push(`Judgment date: ${dateLabel}.`);
  }

  segments.push("Sign in to view the full case summary on Paralegal.");

  return segments.join(" ").slice(0, 320);
}

const CaseCitation = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, state } = useAuthContext();
  const params = useParams();
  const routeCaseNumber = decodeCaseNumberFromParam(params["*"]);
  const [casePreview, setCasePreview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const ctaRef = React.useRef(null);
  const reportsApiBaseUrl = import.meta.env.VITE_REPORTS_API_BASE_URL || "";

  useEffect(() => {
    let cancelled = false;

    const loadCase = async () => {
      setIsLoading(true);
      setLoadError("");

      try {
        const response = await fetch(
          `${reportsApiBaseUrl}/api/cases/public/${encodeURIComponent(routeCaseNumber)}`
        );

        if (!response.ok) {
          if (response.status === 404) {
            throw new Error("No citation was found for this case number.");
          }

          throw new Error("The case citation could not be loaded right now.");
        }

        const result = await response.json();

        if (cancelled) return;

        if (!result) {
          setCasePreview(null);
          setLoadError("No citation was found for this case number.");
          return;
        }

        setCasePreview(result);
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load case citation:", error);
        setCasePreview(null);
        setLoadError(error.message || "The case citation could not be loaded right now.");
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    loadCase();

    return () => {
      cancelled = true;
    };
  }, [routeCaseNumber]);

  const caseNumbers = useMemo(
    () => normalizeCaseNumbers(casePreview?.standard_casenumber),
    [casePreview]
  );

  const primaryCaseNumber = caseNumbers[0] || routeCaseNumber;
  const caseNumberLabel = caseNumbers.join(", ") || routeCaseNumber;
  const canonicalPath = primaryCaseNumber
    ? `/case/${encodeCaseNumberForPath(primaryCaseNumber)}`
    : "/case";
  const summaryPath = primaryCaseNumber
    ? `/summary/${encodeCaseNumberForPath(primaryCaseNumber)}`
    : "/summary";
  const parties = casePreview?.nameofparties?.trim() || "";
  const dateLabel = casePreview?.date ? formatDate(casePreview.date) : "";
  const metadataRows = casePreview?.preview?.metadata_rows || [];
  const previewSections = {
    facts: casePreview?.preview?.facts || "",
    issues: casePreview?.preview?.issues || "",
    applicableLaw: casePreview?.preview?.applicable_law || "",
    casesReferred: casePreview?.preview?.cases_referred_to || [],
  };
  const factsText = previewSections.facts || "";
  const factsIsTruncated = factsText.endsWith("...");
  const factsVisibleText = factsIsTruncated ? factsText.slice(0, -3).trimEnd() : factsText;
  const statutes = previewSections.applicableLaw
    ? previewSections.applicableLaw
        .split("\n")
        .map((line) => line.replace(/^\s*-\s*/, "").trim())
        .filter(Boolean)
    : [];
  const pageTitle = parties
    ? `${parties} | ${caseNumberLabel || "Case Citation"} | Paralegal`
    : caseNumberLabel
      ? `${caseNumberLabel} | Case Citation | Paralegal`
      : "Case Citation | Paralegal";
  const metaDescription = buildMetaDescription({
    parties,
    caseNumber: caseNumberLabel,
    dateLabel,
  });

  useEffect(() => {
    if (!casePreview || !primaryCaseNumber) return;

    if (location.pathname !== canonicalPath) {
      navigate(canonicalPath, { replace: true });
    }
  }, [canonicalPath, casePreview, location.pathname, navigate, primaryCaseNumber]);

  useEffect(() => {
    if (!state.isAuthenticated || !primaryCaseNumber) {
      return;
    }

    navigate(summaryPath, { replace: true });
  }, [navigate, primaryCaseNumber, state.isAuthenticated, summaryPath]);

  const handleViewFullSummary = () => {
    sessionStorage.removeItem("redirectToDocument");
    sessionStorage.removeItem("urlString");
    sessionStorage.setItem("redirectAfterLogin", summaryPath);
    signIn();
  };

  const handleSeeMore = () => {
    ctaRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const structuredData = casePreview
    ? {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: pageTitle,
        url: `https://paralegal.lk${canonicalPath}`,
        description: metaDescription,
        about: {
          "@type": "CreativeWork",
          name: parties || caseNumberLabel || "Case citation",
          identifier: caseNumberLabel || primaryCaseNumber,
          datePublished: casePreview?.date
            ? new Date(casePreview.date).toISOString()
            : undefined,
        },
      }
    : null;

  return (
    <div className="relative">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content={casePreview ? "index,follow" : "noindex,nofollow"} />
        <link rel="canonical" href={`https://paralegal.lk${canonicalPath}`} />
        {structuredData ? (
          <script type="application/ld+json">
            {JSON.stringify(structuredData)}
          </script>
        ) : null}
      </Helmet>

      <SearchLayout isResults={true} showTab={false} showSearch={false}>
        <section className="flex w-full max-w-5xl flex-col px-6 pb-20 pt-[120px] md:px-20 md:pt-[120px]">
          {isLoading ? (
            <div className="rounded-2xl border border-pl-border bg-pl-bg-elevated p-8">
              <p className="text-16 text-pl-text-secondary">Loading case citation...</p>
            </div>
          ) : loadError ? (
            <div className="rounded-2xl border border-pl-border bg-pl-bg-elevated p-8">
              <h1 className="text-2xl font-display font-semibold text-pl-text-primary">
                Case citation not found
              </h1>
              <p className="mt-3 text-pl-text-secondary">{loadError}</p>
            </div>
          ) : (
            <article className="rounded-pl-lg border border-pl-border bg-pl-bg-elevated p-[20px_24px]">
              <div className="flex flex-col gap-3 border-b border-pl-border pb-5">
                <div className="flex flex-col gap-2">
                  <p className="text-12 font-semibold uppercase tracking-[0.2em] text-pl-text-tertiary">
                    Public Case Citation
                  </p>
                  <div className="flex items-baseline gap-3">
                    <h1 className="font-mono text-xl font-medium text-pl-text-primary md:text-2xl">
                      {caseNumberLabel}
                    </h1>
                    {dateLabel ? (
                      <span className="text-13 text-pl-text-tertiary">{dateLabel}</span>
                    ) : null}
                  </div>
                  {parties ? (
                    <p className="text-18 font-medium text-pl-text-primary">{parties}</p>
                  ) : null}
                </div>
              </div>

              {previewSections?.facts ||
              previewSections?.issues ||
              previewSections?.applicableLaw ||
              previewSections?.casesReferred?.length ? (
                <div className="pt-4">
                  {metadataRows.length > 0 ? (
                    <dl className="grid gap-x-6 gap-y-1 py-2 text-14 md:grid-cols-[auto_1fr]">
                      {metadataRows.map(({ label, value }) => (
                        <React.Fragment key={label}>
                          <dt className="text-pl-text-tertiary">{label}</dt>
                          <dd className="mb-1 text-pl-text-secondary md:mb-0">{value}</dd>
                        </React.Fragment>
                      ))}
                    </dl>
                  ) : null}
                  {previewSections.facts ? (
                    <section className="mt-8 first:mt-0">
                      <h2 className="mb-0 mt-0 border-b border-pl-border pb-0 text-18 font-display font-semibold text-pl-text-primary">
                        Facts
                      </h2>
                      <p className="mt-4 leading-8 text-pl-text-secondary">
                        {factsVisibleText}
                        {factsIsTruncated ? (
                          <>
                            ...{" "}
                            <button
                              type="button"
                              onClick={handleSeeMore}
                              className="inline border-0 bg-transparent p-0 font-medium text-pl-accent underline underline-offset-2"
                            >
                              See more
                            </button>
                          </>
                        ) : null}
                      </p>
                    </section>
                  ) : null}

                  {previewSections.issues ? (
                    <ReactMarkdown
                      components={markdownComponents}
                      remarkPlugins={[remarkGfm, remarkBreaks]}
                    >
                      {`## Issues\n${previewSections.issues}`}
                    </ReactMarkdown>
                  ) : null}

                  {previewSections.applicableLaw || previewSections.casesReferred.length ? (
                    <section className="mt-8">
                      <h2 className="mb-0 mt-0 border-b border-pl-border pb-0 text-18 font-display font-semibold text-pl-text-primary">
                        Applicable Law
                      </h2>

                      {statutes.length ? (
                        <>
                          <h3 className="mt-8 mb-2 text-16 font-semibold text-pl-text-primary">
                            Statutes
                          </h3>
                          <ul className="mt-4 list-disc pl-6 text-pl-text-secondary">
                            {statutes.map((statute) => (
                              <li key={statute} className="mt-2">
                                {statute}
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : null}

                      {previewSections.casesReferred.length ? (
                        <>
                          <h3 className="mt-8 mb-2 text-16 font-semibold text-pl-text-primary">
                            Cases Referred to
                          </h3>
                          <ul className="mt-4 list-disc pl-6 text-pl-text-secondary">
                            {previewSections.casesReferred.map((item) => (
                              <li key={item} className="mt-2">
                                {item}
                              </li>
                            ))}
                          </ul>
                        </>
                      ) : null}
                    </section>
                  ) : null}

                  <div className="relative mt-8 overflow-hidden rounded-2xl border border-pl-border bg-pl-bg-subtle/70">
                    <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-pl-bg/40 to-pl-bg" />
                    <div className="blur-[3px] opacity-70">
                      <div className="border-b border-pl-border px-6 pb-0 pt-6">
                        <h2 className="mb-0 mt-0 border-b border-pl-border pb-0 text-18 font-display font-semibold text-pl-text-primary">
                          Further Analysis
                        </h2>
                        <p className="mt-4 leading-8 text-pl-text-secondary">
                          The remaining reasoning, extended treatment of authorities, and full
                          application of the court&apos;s analysis are available after sign in.
                        </p>
                      </div>
                      <div className="px-6 py-5">
                        <p className="mt-4 leading-8 text-pl-text-secondary">
                          Additional sections continue with deeper analysis, fuller factual treatment,
                          more authorities, and the rest of the editorial summary.
                        </p>
                        <ul className="mt-4 list-disc pl-6 text-pl-text-secondary">
                          <li className="mt-2">Detailed reasoning and doctrinal analysis</li>
                          <li className="mt-2">Remaining cases referred to and their relevance</li>
                          <li className="mt-2">Extended application to the facts of the case</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <dl className="grid gap-x-6 gap-y-4 py-6 md:grid-cols-[180px_1fr]">
                  <dt className="text-sm font-medium text-pl-text-tertiary">
                    Standard case number
                  </dt>
                  <dd className="font-mono text-sm text-pl-text-primary">
                    {caseNumberLabel || "N/A"}
                  </dd>

                  <dt className="text-sm font-medium text-pl-text-tertiary">
                    Name of parties
                  </dt>
                  <dd className="text-sm text-pl-text-primary">{parties || "N/A"}</dd>

                  <dt className="text-sm font-medium text-pl-text-tertiary">
                    Judgment date
                  </dt>
                  <dd className="text-sm text-pl-text-primary">{dateLabel || "N/A"}</dd>
                </dl>
              )}

              <div
                ref={ctaRef}
                className="mt-6 rounded-2xl border border-pl-border bg-pl-bg-subtle px-6 py-6 md:px-8"
              >
                <h2 className="text-base font-semibold text-pl-text-primary">
                  Full summary available after sign in
                </h2>
                <p className="mt-3 text-base leading-8 text-pl-text-secondary md:text-[17px]">
                  This public page is intentionally limited. If you do not have an
                  account, sign in for free and get access to the full contents.
                </p>
                <button
                  type="button"
                  onClick={handleViewFullSummary}
                  className="mt-4 rounded-pl border border-pl-accent px-4 py-2 text-sm font-medium text-pl-accent transition-colors duration-150 hover:bg-pl-accent hover:text-white"
                >
                  Sign in to view full summary
                </button>
              </div>
            </article>
          )}
        </section>
      </SearchLayout>
    </div>
  );
};

export default CaseCitation;
